import Link from 'next/link'
import { usePageType } from '../../../lib/hooks/usePageType'
import type { MatchedHeaderCategory } from '../hooks/useHeaderCategories'

type Props = {
  categories: MatchedHeaderCategory[]
  language: string
  numVisible: number
  moreButtonRef: React.RefObject<HTMLButtonElement>
  onToggleDropdown: (open: boolean) => void
  isDropdownOpen: boolean
  onComputeDropdownCoords: (
    coords: { left: number; top: number } | null,
  ) => void
  t: (k: string) => string
  getHref: (category: any, locale: AppLocale) => string
}

export function CityCategoriesDesktop({
  categories,
  language,
  numVisible,
  moreButtonRef,
  onToggleDropdown,
  isDropdownOpen,
  onComputeDropdownCoords,
  t,
  getHref,
}: Props) {
  if (!categories.length) return null
  const { type, citySlug } = usePageType()
  const isCategoryPage = type === 'category'

  const visibleCount = isCategoryPage ? Math.max(numVisible - 1, 0) : numVisible
  const firstSlice = categories.slice(0, visibleCount)
  const rest = categories.slice(visibleCount)
  const allCategoriesLabel =
    t('all-activities') !== 'all-categories'
      ? t('all-activities')
      : 'All categories'

  return (
    <nav className="flex items-center gap-4 flex-nowrap overflow-x-auto overflow-y-visible min-w-0">
      {isCategoryPage && citySlug && (
        <Link
          href={`/c/${citySlug}`}
          className="inline-flex items-center text-sm md:text-base px-3 py-1 rounded-full border border-gray-200 hover:bg-gray-100 whitespace-nowrap max-w-[20rem] overflow-hidden text-ellipsis"
        >
          {allCategoriesLabel}
        </Link>
      )}
      {firstSlice.map((item) => (
        <Link
          key={item._id}
          href={getHref(item.category, language as AppLocale)}
          className="inline-flex items-center text-sm md:text-base px-3 py-1 rounded-full border border-gray-200 hover:bg-gray-100 whitespace-nowrap max-w-[20rem] overflow-hidden text-ellipsis"
        >
          {item.label}
        </Link>
      ))}
      {!!rest.length && (
        <div className="relative" data-header-dropdown-root>
          <button
            ref={moreButtonRef}
            onClick={() => {
              const next = !isDropdownOpen
              if (next) {
                const btn = moreButtonRef.current
                if (btn) {
                  const rect = btn.getBoundingClientRect()
                  const DROPDOWN_WIDTH = 224
                  const GAP = 8
                  const viewportLeft =
                    rect.right + window.scrollX - DROPDOWN_WIDTH
                  const minLeft = window.scrollX + 8
                  const maxLeft =
                    window.scrollX + window.innerWidth - DROPDOWN_WIDTH - 8
                  const left = Math.max(
                    minLeft,
                    Math.min(viewportLeft, maxLeft),
                  )
                  const top = rect.bottom + window.scrollY + GAP
                  onComputeDropdownCoords({ left, top })
                }
              } else {
                onComputeDropdownCoords(null)
              }
              onToggleDropdown(next)
            }}
            data-header-more-button
            className="text-sm md:text-base px-3 py-1 rounded-full border border-gray-200 hover:bg-gray-100 whitespace-nowrap flex items-center gap-1"
            aria-haspopup="true"
            aria-expanded={isDropdownOpen}
          >
            <span>{t('more-categories')}</span>
            <span
              className={`fas fa-chevron-down text-xs transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
              aria-hidden="true"
            />
          </button>
        </div>
      )}
    </nav>
  )
}
